/**
 * NarrativeSettings.tsx
 *
 * Standard React narrative settings page using HTML/CSS
 * Controls AI narrative generation parameters
 */
import React from 'react';
import { useTheme } from '../../services/ThemeContext';
import { useSettings } from '../../services/SettingsContext';
import { ScrollableModal } from './Modal';

interface NarrativeSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const NarrativeSettings: React.FC<NarrativeSettingsProps> = ({ isOpen, onClose }) => {
  const { currentTheme: colors } = useTheme();
  const { narrativeSettings, setNarrativeSettings } = useSettings();

  const handleSettingChange = (key: keyof typeof narrativeSettings, value: string) => {
    if (key === 'defaultDirectives') {
      setNarrativeSettings({ [key]: value });
    } else {
      const numberValue = parseInt(value, 10);
      if (!isNaN(numberValue)) {
        setNarrativeSettings({ [key]: numberValue });
      }
    }
  };

  if (!isOpen) return null;

  return (
    <ScrollableModal isOpen={isOpen} onClose={onClose} title="Narrative Settings">
      {/* Max Narrative Paragraphs */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4, color: colors.textMain }}>
          Max Narrative Paragraphs
        </div>
        <div style={{ fontSize: 12, marginBottom: 8, color: colors.textMuted }}>
          Controls the maximum length of the AI's response. (Range: 1-10, Default: 2)
        </div>
        <input
          type="number"
          min={1}
          max={10}
          value={narrativeSettings.maxNarrativeParagraphs}
          onChange={e => handleSettingChange('maxNarrativeParagraphs', e.target.value)}
          style={{
            width: '100%',
            borderWidth: 1,
            borderColor: colors.border,
            borderStyle: 'solid',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            backgroundColor: colors.surface,
            color: colors.textMain,
          }}
        />
      </div>

      {/* Suggested Actions Count */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4, color: colors.textMain }}>
          Suggested Actions Count
        </div>
        <div style={{ fontSize: 12, marginBottom: 8, color: colors.textMuted }}>
          Controls how many suggested actions the AI provides. (Range: 1-10, Default: 3)
        </div>
        <input
          type="number"
          min={1}
          max={10}
          value={narrativeSettings.suggestedActionsCount}
          onChange={e => handleSettingChange('suggestedActionsCount', e.target.value)}
          style={{
            width: '100%',
            borderWidth: 1,
            borderColor: colors.border,
            borderStyle: 'solid',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            backgroundColor: colors.surface,
            color: colors.textMain,
          }}
        />
      </div>

      {/* Default AI Directives */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4, color: colors.textMain }}>
          Default AI Directives
        </div>
        <div style={{ fontSize: 12, marginBottom: 8, color: colors.textMuted }}>
          Style directives that will be applied to all new stories by default. Leave empty for no default.
        </div>
        <textarea
          value={narrativeSettings.defaultDirectives}
          onChange={e => handleSettingChange('defaultDirectives', e.target.value)}
          placeholder="E.g., Soldier's perspective, healed follower, innocent child"
          rows={4}
          style={{
            width: '100%',
            borderWidth: 1,
            borderColor: colors.border,
            borderStyle: 'solid',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            backgroundColor: colors.surface,
            color: colors.textMain,
            resize: 'vertical',
          }}
        />
      </div>
    </ScrollableModal>
  );
};

export { NarrativeSettings };
